import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSendAffidavitMutation } from 'api/tunnelSteps';
import { texts } from 'utils/languages/fr';
import { userSelectors } from 'reducers';
import { documentApi } from 'api/main';
import { checkAcceptedFileTypes, getFilenameFromHeaders, validateFileSize } from 'utils/helpers';
import { docTypes } from 'utils/constants';

import { CustomButton, FileErrorMessage, SubmitButton, Loader, FileInputButton } from 'components';
import { Box, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    uploadRow: {
        marginTop: 5,
        marginLeft: '40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            flexDirection: 'column',
        },
    },
    fileName: {
        marginLeft: '40px',
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            textAlign: 'center',
        },
    },
    infoAttestation: {
        marginBottom: 10,
        marginRight: 15,
        [theme.breakpoints.down('sm')]: {
            textAlign: 'center',
        },
    },
    buttonContainer: {
        flexBasis: '40%',
        width: '100%',
        [theme.breakpoints.down('md')]: {
            flexBasis: 0,
        },
    },
}));

const AttestationSurHonneur = ({ onSubmit }) => {
    const classes = useStyles();

    const [
        sendAffidavit,
        { isLoading: ashSendLoader, error: submitError, isSuccess: isAttestationSubmitted, reset },
    ] = useSendAffidavitMutation();
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileChanged, setFileChanged] = useState(false);
    const [template, setTemplate] = useState(null);
    const [currentFileName, setCurrentFileName] = useState(null);
    const [volatileFileName, setVolatileFileName] = useState(null);
    const [ashLoader, setAshLoader] = useState(false);
    const [lastUploadedFileLoader, setLastUploadedFileLoader] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (tunnelSubscription?.withAffidavitDocument && currentFileName === null) {
            setLastUploadedFileLoader(true);
            documentApi
                .downloadDocument({
                    type: docTypes.AFFIDAVIT,
                    withUpload: true,
                    subscriptionId: tunnelSubscription?.id,
                })
                .then(response => {
                    const fileName =
                        getFilenameFromHeaders(response.headers) || 'attestation-sur-honneur.pdf';
                    if (mounted) {
                        setVolatileFileName(null);
                        setCurrentFileName(fileName);
                        setLastUploadedFileLoader(false);
                    }
                })
                .catch(() => {
                    if (mounted) setLastUploadedFileLoader(false);
                });
        }

        return () => (mounted = false);
    }, [currentFileName, tunnelSubscription]);

    const handleFileChange = useCallback(
        e => {
            const file = e.target.files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    if (validateFileSize(file)) {
                        setUploadedFile(file);
                        setVolatileFileName(file?.name);
                        reset();
                        setFileChanged(true);
                    } else {
                        setFileSizeError(true);
                    }
                },
                () => {
                    setFileTypeError(true);
                    setFileSizeError(false);
                }
            );
        },
        [reset]
    );

    const handleSubmitAttestation = useCallback(() => {
        var formData = new FormData();
        formData.append('file', uploadedFile);

        sendAffidavit({
            payload: formData,
            callback: () => {
                setFileChanged(false);
                onSubmit();
            },
        });
    }, [onSubmit, sendAffidavit, uploadedFile]);

    const downloadTemplate = url => {
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = 'attestation-sur-honneur.docx';
            link.click();
        }
    };

    const getTemplate = useCallback(() => {
        if (!template) {
            setAshLoader(true);
            documentApi
                .downloadDocument({
                    type: docTypes.AFFIDAVIT,
                    withUpload: false,
                    subscriptionId: tunnelSubscription?.id,
                })
                .then(response => {
                    const bytes = new Uint8Array(response.data);
                    const blob = new Blob([bytes], {
                        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    });
                    const url = window.URL.createObjectURL(blob);
                    setTemplate(url);
                    setAshLoader(false);
                    downloadTemplate(url);
                });
        } else {
            downloadTemplate(template);
        }
    }, [template, tunnelSubscription]);

    // Handle mutation errors in side effect
    useEffect(() => {
        if (submitError?.status === 415) {
            setFileSizeError(true);
        }
    }, [submitError]);

    if (lastUploadedFileLoader) {
        return <Loader staticOnly />;
    }

    return (
        <Box marginTop="60px" marginBottom="20px" display="flex" flexDirection="column">
            <Typography variant="body1" className={classes.infoAttestation} gutterBottom>
                {texts.INFO_ATTESTATION_SUR_HONNEUR}
            </Typography>
            <div className={classes.uploadRow}>
                <Typography variant="body1" className={classes.infoAttestation} gutterBottom>
                    {texts.INFO_ATTESTATION_SUR_HONNEUR_OPTION_1}
                </Typography>
                <div className={classes.buttonContainer}>
                    <CustomButton
                        label={texts.DOWNLOAD_FILE_BUTTON}
                        onClick={getTemplate}
                        loading={ashLoader}
                    />
                </div>
            </div>
            <div className={classes.uploadRow}>
                <Typography variant="body1" className={classes.infoAttestation} gutterBottom>
                    {texts.INFO_ATTESTATION_SUR_HONNEUR_OPTION_2}
                </Typography>
                <div className={classes.buttonContainer}>
                    <FileInputButton
                        showIcon
                        id="affidavit"
                        label={texts.UPLOAD_FILE_BUTTON}
                        onFileChange={handleFileChange}
                    />
                    {fileTypeError && <FileErrorMessage variant="type" />}
                    {fileSizeError && <FileErrorMessage variant="size" />}
                </div>
            </div>
            <Typography className={classes.fileName} variant="body2" gutterBottom>{`${
                texts.UPLOADED_FILE_NAME
            } : ${volatileFileName || currentFileName || texts.AUCUN}`}</Typography>
            {isAttestationSubmitted && (
                <Typography sx={{ marginTop: '10px' }} align="center" variant="body2" gutterBottom>
                    {texts.UPLOADED_ATTESTATION_SUR_HONNEUR_SUCCESS}
                </Typography>
            )}
            <SubmitButton
                text={texts.ENVOYER}
                onClick={handleSubmitAttestation}
                disabled={!uploadedFile || !fileChanged || ashSendLoader}
                loading={ashSendLoader}
            />
        </Box>
    );
};

export default AttestationSurHonneur;
